import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageCircle, FiX, FiUser } = FiIcons;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Chat Bubble Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
      >
        <motion.button
          onClick={toggleChat}
          className={`
            relative w-16 h-16 rounded-full shadow-lg transition-all duration-300
            ${isOpen 
              ? 'bg-red-500 hover:bg-red-600' 
              : 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800'
            }
            text-white flex items-center justify-center
            hover:shadow-xl transform hover:scale-110
          `}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          aria-label={isOpen ? "Close chat" : "Open chat with insurance agent"}
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <SafeIcon icon={FiX} className="h-6 w-6" />
              </motion.div>
            ) : (
              <motion.div
                key="chat"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <SafeIcon icon={FiMessageCircle} className="h-6 w-6" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Notification Pulse (when closed) */}
          {!isOpen && (
            <motion.div
              className="absolute -top-1 -right-1 w-4 h-4 bg-secondary-500 rounded-full"
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [1, 0.7, 1]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          )}
        </motion.button>

        {/* Tooltip when hovering over closed button */}
        {!isOpen && (
          <motion.div
            className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-800 text-white text-sm rounded-lg whitespace-nowrap opacity-0 pointer-events-none transition-opacity duration-200 hover:opacity-100"
            style={{ transform: 'translateX(50%)' }}
          >
            Chat with Insurance Agent
            <div className="absolute top-full right-1/2 transform translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
          </motion.div>
        )}
      </motion.div>

      {/* Chat Window Container - Always mounted but hidden when closed */}
      <div
        className={`fixed bottom-24 right-6 z-40 transition-all duration-300 ${
          isOpen 
            ? 'opacity-100 visible transform translate-y-0' 
            : 'opacity-0 invisible transform translate-y-4 pointer-events-none'
        }`}
      >
        <motion.div
          initial={false}
          animate={{ 
            scale: isOpen ? 1 : 0.8,
            opacity: isOpen ? 1 : 0
          }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          style={{ transformOrigin: 'bottom right' }}
        >
          <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden max-w-md w-80 md:w-96">
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-4">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 p-2 rounded-full">
                  <SafeIcon icon={FiUser} className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">Insurance Agent</h3>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm opacity-90">Online now</span>
                  </div>
                </div>
                <button
                  onClick={toggleChat}
                  className="text-white/80 hover:text-white transition-colors p-1"
                  aria-label="Close chat"
                >
                  <SafeIcon icon={FiX} className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Chat Iframe Container - Always rendered to preserve session */}
            <div className="relative">
              <iframe 
                src="https://studio.pickaxe.co/_embed/Business_Insurance_Specialist__Chat_Agent_O5EHE?d=deployment-5d294cce-f428-43f7-8630-d06a450d2982" 
                style={{ 
                  width: '100%', 
                  height: '500px',
                  border: 'none',
                  borderRadius: '0',
                  display: 'block'
                }}
                frameBorder="0" 
                allow="microphone"
                title="Insurance Agent Chat"
                loading="lazy"
              />
            </div>

            {/* Footer */}
            <div className="p-3 bg-gray-50 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center">
                Powered by Burbank Insurance Services • 
                <span className="text-primary-600 ml-1">CA License #6013543</span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default ChatWidget;